"use client";
import React, { useEffect, useState } from "react";
import HowItWorksSectionMobile from "./HowItWorksSectionMobile";
import HowItWorksSectionPC from "./HowItWorksSectionPC";
import type { HowItWorksSectionData } from "@/types/cms";

interface Props {
  /** Optional manual override for testing ("mobile" | "desktop") */
  forceMode?: "mobile" | "desktop";
  /** If true, hides layout shift by rendering nothing until detection completes */
  deferInitialRender?: boolean;
  /** CMS data for content */
  data?: HowItWorksSectionData;
}

// Heuristic mobile detection: userAgent OR small viewport OR coarse pointer
function isMobileEnv(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  const uaMatch = /android|iphone|ipad|ipod|opera mini|iemobile|mobile/.test(
    ua
  );
  const narrow = window.innerWidth < 1080; // tailwind md breakpoint
  const coarse =
    window.matchMedia && window.matchMedia("(pointer:coarse)").matches;
  return uaMatch || narrow;
}

export default function HowItWorksSectionResponsive({
  forceMode,
  deferInitialRender = false,
  data,
}: Props) {
  const [isMobile, setIsMobile] = useState<boolean | null>(
    forceMode ? forceMode === "mobile" : null
  );

  useEffect(() => {
    if (forceMode) return; // manual override
    const update = () => setIsMobile(isMobileEnv());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [forceMode]);

  // Optionally defer first render to avoid hydration mismatch flash
  if (deferInitialRender && isMobile === null) return null;

  // Fallback before detection finishes -> desktop (PC) section
  const mobile = forceMode ? forceMode === "mobile" : isMobile ?? false;
  return mobile ? (
    <HowItWorksSectionMobile data={data} />
  ) : (
    <HowItWorksSectionPC data={data} />
  );
}
